import React, { useState, useEffect, useCallback } from 'react'
import classNames from 'classnames'
import { useAnalyticsTracking } from 'src/utils/GoogleAnalytics'
import fontStyles from 'src/assets/styles/fontStyles.module.scss'
import generalStyles from 'src/assets/styles/generalStyles.module.scss'
import elementStyles from 'src/components/Elements/elements.module.scss'
import Button from 'src/components/Elements/Button'
import useUpdateEventRegStatus from 'src/queries/events/useUpdateEventRegStatus'
import Checkbox from 'src/components/Elements/Inputs/Checkbox'
import RadioButton from 'src/components/Elements/Inputs/RadioButton'
import Select from 'src/components/Elements/Inputs/Select'

const RegQuestions = ({ closeModal, data }) => {
  const { questions } = data
  const [answers, setAnswers] = useState({})

  const { mutate } = useUpdateEventRegStatus()
  const googleTracking = useAnalyticsTracking()
  const [submitError, setSubmitError] = useState('')

  useEffect(() => {
    if (questions && questions.length > 0) {
      const initialAnswers = questions.reduce((answersMap, question) => {
        switch (question.survey_question_type_key) {
          case 'multiple_choice':
          case 'drop_down':
          case 'range_select':
          case 'true_false':
            answersMap[question.id] = null
            break
          case 'checkboxes': {
            const checkboxesOptions = {}
            question.question_options.forEach((opt) => {
              checkboxesOptions[opt.id] = false
            })
            answersMap[question.id] = checkboxesOptions
            break
          }
          case 'checkbox':
            answersMap[question.id] = false
            break
          case 'text_box':
          case 'text_area':
            answersMap[question.id] = ''
            break
          case 'rating_with_optional_text':
            answersMap[question.id] = { rating: null, text: '' }
            break
        }
        return answersMap
      }, {})
      setAnswers(initialAnswers)
    }
  }, [questions])

  const onCheckboxesClick = (optionId, questionId, value) => {
    const prevCheckboxValues = answers[questionId]
    prevCheckboxValues[optionId] = value
    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [questionId]: prevCheckboxValues
    }))
  }

  const onCheckboxClick = (questionId, value) => {
    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [questionId]: value
    }))
  }

  const onMultipleChoiceSelection = (questionId, optionId) => {
    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [questionId]: optionId
    }))
  }

  const onTrueFalseValueSelection = (questionId, value) => {
    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [questionId]: value
    }))
  }

  const onRangeValueSelection = (questionId, value) => {
    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [questionId]: value
    }))
  }

  const onSelectOptionSelection = (questionId, optionId) => {
    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [questionId]: optionId
    }))
  }

  const onRatingWithTextValueRatingSelection = (questionId, value) => {
    const prevRatingWithTextValues = answers[questionId]
    prevRatingWithTextValues.rating = value
    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [questionId]: prevRatingWithTextValues
    }))
  }

  const onRatingWithTextValueTextValueChanged = (questionId, value) => {
    const prevRatingWithTextValues = answers[questionId]
    prevRatingWithTextValues.text = value
    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [questionId]: prevRatingWithTextValues
    }))
  }

  const onTextValueChanged = (questionId, value) => {
    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [questionId]: value
    }))
  }

  const submitHandler = useCallback(() => {
    if (submitButtonInactive()) {
      setSubmitError('Please answer all questions')
      return
    } else {
      setSubmitError('')
    }
    const surveyAnswers = questions?.reduce((obj, question) => {
      switch(question.survey_question_type_key) {
        case 'multiple_choice':
          obj[question.id] = question.question_options
            .find((opt) => opt.id === answers[question.id])?.name
          break
        case 'checkboxes':
          obj[question.id] = question.question_options
            .filter((opt) => answers[question.id][opt.id] === true)
            ?.map((e) => e.name)
            ?.join(', ')
          break
        case 'checkbox':
          obj[question.id] = answers[question.id] ? 'checked' : null
          break
        case 'text_box':
        case 'text_area':
          obj[question.id] = answers[question.id]
          break
        case 'range_select':
        case 'drop_down':
          obj[question.id] = question.question_options.find((option) => option.id === answers[question.id])?.name
          break
        case 'true_false':
          obj[question.id] = answers[question.id] ? 'True' : 'False'
          break
        case 'rating_with_optional_text':
          obj[question.id] = [answers[question.id].rating, answers[question.id].text]
          break
      }
      return obj
    }, {})
    mutate({
      regObj: {
        crm_contact_id: data.crm_contact_id,
        event_id: data.event_id,
        survey_user_id: data.survey_user_id,
        survey_answers: surveyAnswers,
        current_event: data.current_event
      }
    })
    googleTracking(
      'additional_reg',
      data.current_event ? 'event_page' : 'app_home_page',
      {
        event_id: `${data.event_id}`,
        brand: data.brand,
        region: data.region,
        event_name: data.event_name.substring(0, 36),
        event_type: data.event_type
      }
    )
    googleTracking(
      'event_reg',
      data.current_event ? 'event_page' : 'app_home_page',
      {
        event_id: `${data.event_id}`,
        brand: data.brand,
        region: data.region,
        event_name: data.event_name.substring(0, 36),
        event_type: data.event_type
      }
    )
    closeModal()
  }, [questions, data, answers])

  const renderQuestions = () => {
    if (Object.keys(answers).length === 0) {
      return null
    }

    return data.questions.map((question, index) => {
      const questionText = (
        <div
          dangerouslySetInnerHTML={{ __html: question.question_text }}
          style={{ marginBottom: 10, marginTop: 20 }}
        />
      )
      let questionContents = null
      switch (question.survey_question_type_key) {
        case 'text_box':
          questionContents = (
            <div style={{ width: '400px', height: '50px' }}>
              <textarea
                className={classNames(
                  elementStyles.textBox,
                  fontStyles.psmalllsinneutral08onyxgartnersans15pt
                )}
                style={{ width: '360px', height: '15px' }}
                id={question.id}
                data-testid={'text-box-' + question.id}
                onChange={(e) => onTextValueChanged(question.id, e.target.value)}
                value={answers[question.id] || ''}
                rows="1"
                placeholder="Write here..."
              />
            </div>
          )
          break
        case 'true_false':
          questionContents = (
            <div
              className={generalStyles.flexColumn}
              style={{ marginTop: 20, paddingLeft: 1, width: '100%' }}
              data-testid={`answer${index}`}
            >
              <div
                style={{ marginBottom: '20px', minHeight: '24px' }}
                className={generalStyles.flexRow}
                key={`radio-${question.id}-true`}
              >
                <RadioButton
                  key={`radio-cmpt-${question.id}-true`}
                  id={`${question.id}-true`}
                  group={question.id}
                  label={'True'}
                  value={true}
                  selected={answers[question.id] === true}
                  textMarginOverride={generalStyles.mr8}
                  onClick={() => onTrueFalseValueSelection(question.id, true)}
                />
              </div>
              <div
                style={{ marginBottom: '20px', minHeight: '24px' }}
                className={generalStyles.flexRow}
                key={`radio-${question.id}-false`}
              >
                <RadioButton
                  key={`radio-cmpt-${question.id}-false`}
                  id={`${question.id}-false`}
                  group={question.id}
                  label={'False'}
                  value={false}
                  selected={answers[question.id] === false}
                  textMarginOverride={generalStyles.mr8}
                  onClick={() => onTrueFalseValueSelection(question.id, false)}
                />
              </div>
            </div>
          )
          break
        case 'range_select':
          questionContents = (
            <div
              className={ generalStyles.flexColumn }
              style={{ marginTop: 20, paddingLeft: 1, width: '100%', justifyContent: 'space-between' }}
              data-testid={`answer${index}`}
            >
              {question.question_options.map((opt, optIndex) => (
                <div
                  style={{ marginBottom: '20px', minHeight: '24px' }}
                  className={generalStyles.flexRow}
                  key={`radio-${question.id}-${opt.id}-${optIndex}`}
                >
                  <RadioButton
                    key={`radio-cmpt-${opt.id}-${optIndex}`}
                    id={opt.id}
                    group={question.id}
                    label={opt.name}
                    value={opt.name}
                    selected={answers[question.id] === opt.id}
                    textMarginOverride={generalStyles.mr8}
                    onClick={() => onRangeValueSelection(question.id, opt.id)}
                  />
                </div>
              ))}
            </div>
          )
          break
        case 'multiple_choice':
          questionContents = (
            <div
              className={generalStyles.flexColumn}
              style={{ marginTop: 20, paddingLeft: 1, width: '100%' }}
              data-testid={`answer${index}`}
            >
              {question.question_options.map((opt) => (
                <div
                  style={{ marginBottom: '20px', minHeight: '24px' }}
                  className={generalStyles.flexRow}
                  key={`radio-${question.id}-${opt.id}`}
                >
                  <RadioButton
                    key={`radio-cmpt-${question.id}-${opt.id}`}
                    id={opt.id}
                    group={question.id}
                    label={opt.name}
                    value={opt.id}
                    selected={answers[question.id] === opt.id}
                    textMarginOverride={generalStyles.mr8}
                    onClick={(value) => onMultipleChoiceSelection(question.id, value)}
                  />
                </div>
              ))}
            </div>
          )
          break
        case 'text_area':
          questionContents = (
            <div style={{ width: '400px', height: '125px' }}>
              <textarea
                className={classNames(
                  elementStyles.textareaInputBorder,
                  elementStyles.smallTextArea,
                  fontStyles.psmalllsinneutral08onyxgartnersans15pt
                )}
                style={{ width: '360px', height: '83px' }}
                id={question.id}
                data-testid={'text-area-' + question.id}
                onChange={(e) => onTextValueChanged(question.id, e.target.value)}
                value={answers[question.id] || ''}
                rows="4"
                placeholder="Write here..."
              />
            </div>
          )
          break
        case 'checkboxes':
          questionContents = (
            <>
              {question.question_options.map((opt) => (
                <div
                  key={opt.id}
                  className={classNames(
                    generalStyles.flexRow,
                    generalStyles.mb20
                  )}
                  data-testid={`answer${index}`}
                  style={{ alignItems: 'center', width: '100%' }}
                >
                  <Checkbox
                    id={`checkbox-${opt.id}`}
                    onChange={(id, selected) => onCheckboxesClick(opt.id, question.id, !selected)}
                    name={opt.name}
                    selected={answers[question.id][opt.id]}
                    boldLabel={false}
                  />
                </div>
              ))}
            </>
          )
          break
        case 'checkbox':
          questionContents = (
            <div
              className={classNames(generalStyles.flexRow)}
              style={{ marginBottom: '20x' }}
            >
              <Checkbox
                id={question.id}
                onChange={(id, selected) => onCheckboxClick(question.id, !selected)}
                name={question.name}
                selected={answers[question.id]}
              />
            </div>
          )
          break
        case 'rating_with_optional_text':
          questionContents = (
            <div
              className={generalStyles.flexColumn}
              style={{ paddingLeft: 1, width: '100%', justifyContent: 'space-between' }}
              data-testid={`answer${index}`}
            >
              {question.question_options.map((opt, optIndex) => (
                <div
                  style={{ marginBottom: '16px', minHeight: '24px' }}
                  className={generalStyles.flexRow}
                  key={`radio-${question.id}-${opt.id}-${optIndex}`}
                >
                  <RadioButton
                    key={`radio-cmpt-${opt.id}-${optIndex}`}
                    id={opt.id}
                    group={question.id}
                    label={opt.name}
                    value={opt.name}
                    selected={answers[question.id].rating === opt.id}
                    textMarginOverride={generalStyles.mr8}
                    onClick={() => onRatingWithTextValueRatingSelection(question.id, opt.id)}
                  />
                </div>
              ))}
              <p>{question.ratings_optional_text_label}</p>
              <div style={{ width: '400px', height: '125px' }}>
                <textarea
                  className={classNames(
                    elementStyles.textareaInputBorder,
                    elementStyles.smallTextArea,
                    fontStyles.psmalllsinneutral08onyxgartnersans15pt,
                  )}
                  style={{ width: '360px', height: '83px' }}
                  id={question.id}
                  data-testid={'rating-text-area-' + question.id}
                  onChange={(e) => onRatingWithTextValueTextValueChanged(question.id, e.target.value)}
                  value={answers[question.id].text || ''}
                  rows="4"
                  placeholder="Write here..."
                />
              </div>
            </div>
          )
          break
        case 'drop_down':
          questionContents = (
            <div
              className={classNames(generalStyles.flexRow)}
              style={{ marginBottom: '20px' }}
            >
              <Select
                id={question.id}
                onChange={(value) => onSelectOptionSelection(question.id, value)} 
                value={answers[question.id]}
                options={question.question_options.reduce((optionsList, option) => {
                  optionsList.push({name: option.name, value: option.id})
                  return optionsList
                }, [])}
              />
            </div>
          )
          break
        default:
          questionContents = <></>
          break
      }
      return (
        <div
          key={question.id}
          data-testid={`${question.survey_question_type_key}_question-${index}`}
        >
          <span
            className={fontStyles.psmalllsinneutral08onyxgartnersans15pt}
            data-testid="questionBody"
          >
            {questionText}
          </span>
          {questionContents}
        </div>
      )
    })
  }

  const submitButtonInactive = () => {
    for (const question of questions) {
      if (!question.is_required) {
        continue
      }
      switch (question.survey_question_type_key) {
        case 'checkboxes': {
          let hasAnswered = false
          for (const checkboxAnswer in answers[question.id]) {
            if (answers[question.id][checkboxAnswer] === true) {
              hasAnswered = true
              break
            }
          }
          if (!hasAnswered) {
            return true
          }
          break
        }
        case 'checkbox':
          if (answers[question.id] === false || answers[question.id] === null) {
            return true
          }
          break
        case 'range_select':
        case 'true_false':
        case 'drop_down':
        case 'multiple_choice':
          if (answers[question.id] === null) {
            return true
          }
          break
        case 'text_box':
        case 'text_area':
          if (answers[question.id] === '') {
            return true
          }
          break
        case 'rating_with_optional_text':
          if (
            answers[question.id].rating === null
          ) {
            return true
          }
          break
      }
    }
    return false
  }

  return (
    <>
      <div style={{ minHeight: '284px', maxWidth: '399px' }}>
        <div style={{ minHeight: '202px', marginTop: '20px' }}>
          {renderQuestions()}
        </div>
        <div className={generalStyles.mt40}>
          <p className={classNames(fontStyles.h5italicred)}>{submitError}</p>
          <Button
            disabled={false}
            color={'lightBase'}
            onClick={submitHandler}
            label={'Submit'}
            size={'med'}
            style={{
              height: 42,
              width: 96,
              paddingLeft: 20,
              paddingRight: 20,
              paddingTop: 13,
              paddingBottom: 13
            }}
          />
        </div>
      </div>
    </>
  )
}

export default RegQuestions
